<template>
  <div
    :class="!dialog ? 'column items-center': ''"
  >
    <component
      :is="dialog ? 'q-dialog': 'div'"
      v-if="modalType === 'maximized-modal' || modalType === 'in-page'"
      v-model="showDialog"
      :class="{ 'full-width': !dialog }"
      :style="modelUpdateAvailable ? 'border: solid red 3px' :''"
      :maximized="true"
      transition-show="fade-in"
      transition-hide="fade-out"
      @keyup.escape="close(false)"
    >
      <q-card
        :style="{
          width: maximizedWidth,
          height: maximizedHeight,
          zoom: $kiosk && $isLandscapeView() && zoom !== 1 ? `calc(1 / ${zoom})` : undefined,
          'font-size': $kiosk && $isLandscapeView() && zoom !== 1 ? `${zoom}rem` : undefined
        }"
        :bordered="!dialog && !borderless"
        :flat="!dialog"
      >
        <q-form
          ref="formRef"
          greedy
          class="full-height"
        >
          <model-out-of-date-banner v-if="modelUpdateAvailable" />
          <model-error-banner :shown="hasErrors || modelDestroyed || !!$slots.errors">
            <template v-if="modelDestroyed">
              Model destroyed
            </template>
            <slot
              v-else-if="!!$slots.errors"
              name="errors"
            />
          </model-error-banner>
          <!-- This notice banner is temporary, to prevent confusion until all input fields have been replaced with to ModelFormInput components. -->
          <translation-notice-banner
            v-if="showTranslationNotice"
            :shown="model.isTranslation"
          >
            <slot name="translation-notice" />
          </translation-notice-banner>
          <div
            :class="{ 'row q-ma-sm items-center q-gutter-sm': !btnFloatingRight }"
          >
            <div
              class="col row justify-start items-center"
              :class="{ 'absolute-top-right z-top': btnFloatingRight }"
              :style="{ 'flex-grow': flexGrow ? '1' : undefined }"
            >
              <q-btn
                v-if="dialog"
                icon="close"
                flat
                round
                dense
                :class="{ 'q-pa-sm bg-responsive q-ma-md shadow-3': btnFloatingRight }"
                @click="close(false)"
              />
              <q-btn
                v-if="embeddedInModelTable"
                class="q-ml-sm gt-xs"
                dense
                icon="arrow_back"
                :disable="model.loading || model.isDirty || model.isFirst"
                @click="previous()"
              />
              <q-btn
                v-if="embeddedInModelTable"
                class="gt-xs"
                dense
                icon="arrow_forward"
                :disable="model.loading || model.isDirty || model.isLast"
                @click="next()"
              />
              <div
                v-if="embeddedInModelTable && model.parent && model.parent.data"
                class="q-ml-sm gt-xs"
              >
                {{ model.findIndex + 1 }} of {{ model.parent.data.length }}
              </div>
              <translate-language-select
                v-if="shouldShowLanguageSelect"
                :model-value="currentLanguage"
                class="q-ml-md"
                :loading="model.loading"
                :disable="shouldDisableLanguageSelect"
                menu-header-text="Edit Translations"
                @update:model-value="changeTranslationLanguage"
              />
            </div>
            <div
              v-if="!hideTitle"
              class="col-auto text-h6"
            >
              <slot
                name="title"
              >
                <div
                  class="row items-center"
                >
                  <template v-if="title">
                    {{ title }}
                  </template>
                  <template v-else>
                    {{ actionDisplayName }} {{ modelLabel || model.humanTypeName }} ({{ model.isLocal ? 'New' : model.id }})
                  </template>
                  <slot name="model-level-icon">
                    <model-level-icon
                      v-if="currentUser && currentUser.employee"
                      :model="model"
                    />
                  </slot>
                </div>
              </slot>
            </div>
            <div
              class="col row justify-end items-center"
              style="flex-grow: 1"
            >
              <viewing-users
                v-if="allViewingUsers.length"
                :other-viewing-users="viewingUsersNotMe"
              />
              <slot name="header-controls" />
              <!-- Uncomment for debugging <div v-if="!hasErrors && model.isDirty">
              <q-icon
                class="q-mr-sm"
                size="sm"
                name="info_outline"
                @click="exposeModel"
              />
              <q-tooltip>
                {{ model.describeDirty }}
              </q-tooltip>
            </div> -->
              <link-to-sites-button
                v-if="canHaveChildSites"
                :model="model"
                :disable="model.isDirty || creating"
                :loading="saveAndClosedClicked"
                @click="showLinkToSitesDialog = true"
              />
              <q-btn
                v-if="!model.isSubmodel"
                class="gt-xs"
                icon="refresh"
                round
                :disable="model.loading || model.dirty"
                @click="fetchUpdatedData()"
              >
                <q-tooltip>Reload Data</q-tooltip>
              </q-btn>
              <q-btn
                v-if="$admin && !model.isSubmodel && model.versioned"
                class="gt-xs"
                icon="history"
                round
                :disable="model.loading || model.dirty"
                @click="showHistory(model.name, model.id)"
              >
                <q-tooltip>View History</q-tooltip>
              </q-btn>
              <audit-button
                v-if="model.modelAudits && model.modelAudits.length"
                :model="model"
                @click="$refs.auditDialog.show()"
              />
              <template v-if="lockedAndNotOwner">
                <take-edit-lock-button
                  :locked-and-not-owner="lockedAndNotOwner"
                  :editing-user="editingUser"
                  @click="lock()"
                />
              </template>
              <template v-else-if="!hideHeaderButtons">
                <q-btn
                  v-if="!creating"
                  class="q-mr-sm gt-xs"
                  :label="revertLabel"
                  :outline="btnOutline"
                  :rounded="btnRound"
                  :disable="model.loading || !model.isDirty"
                  :class="{
                    'fixed-bottom q-mx-auto z-max q-py-sm q-mb-lg': btnPositionFixedBottom,
                    'absolute-bottom-left q-mx-md q-mb-lg z-max': btnPositionAbsoluteBottomLeft,
                  }"
                  :style="{
                    width: buttonWidth,
                    'margin-bottom': (btnPositionFixedBottom || btnPositionAbsoluteBottomLeft) ? '70px' : undefined
                  }"
                  @click="revert()"
                />
                <q-btn
                  v-if="($q.screen.gt.xs || creating) && model.isManagePermitted"
                  class="q-mr-sm"
                  :rounded="btnRound"
                  :size="btnLarge && creating ? 'lg' : undefined"
                  :class="{
                    'fixed-bottom q-mx-auto z-max q-py-sm q-mb-md': btnPositionFixedBottom,
                    'absolute-bottom-left q-mx-md q-mb-lg z-max': btnPositionAbsoluteBottomLeft,
                    'bg-responsive': true,
                    'q-mb-xl': btnPositionFixedBottom && isAppView && $q.platform.is.ios
                  }"
                  :style="{ width: buttonWidth }"
                  :disable="(!model.isDirty && !creating) || lockedAndNotOwner || !model.isManagePermitted"
                  :color="showAndClose || btnPositionFixedBottom ? 'white' : 'primary'"
                  :text-color="(showAndClose || !$isColorDark(currentSite.primaryColor) || btnPositionFixedBottom) ? 'black' : 'white'"
                  :loading="saveClicked"
                  autofocus
                  data-test-id="model-form-update"
                  @click="save((showAndClose) ? false : closeOnSave)"
                >
                  {{ saveLabel }}
                  <slot
                    v-if="btnShowTitle && ($q.screen.lt.sm || !$isLandscapeView())"
                    name="title"
                  >
                    {{ title }}
                  </slot>
                  <q-icon
                    v-if="lockedAndNotOwner"
                    name="lock"
                  />
                  <action-not-permitted-tooltip v-if="!model.isManagePermitted" />
                </q-btn>
                <q-btn
                  v-else-if="model.isManagePermitted"
                  :icon="btnShowTitle ? undefined : 'save'"
                  class="q-mr-sm"
                  :disable="(!model.isDirty && !creating) || !model.isManagePermitted"
                  :size="btnLarge ? 'lg' : undefined"
                  :round="!btnShowTitle"
                  :rounded="btnRound"
                  :color="btnPositionFixedBottom ? 'white' : 'primary'"
                  :text-color="(showAndClose || !$isColorDark(currentSite.primaryColor) || btnPositionFixedBottom) ? 'black' : 'white'"
                  :loading="saveClicked"
                  :class="{
                    'fixed-bottom q-mx-auto z-max q-py-sm q-mb-md': btnPositionFixedBottom,
                    'absolute-bottom-left q-mx-md q-mb-xl z-max': btnPositionAbsoluteBottomLeft,
                    'q-mb-xl': btnPositionFixedBottom && isAppView
                  }"
                  :style="{ width: buttonWidth }"
                  @click="save(true)"
                >
                  {{ saveLabel }}
                  <slot
                    v-if="btnShowTitle && ($q.screen.lt.sm || !$isLandscapeView())"
                    name="title"
                  >
                    {{ title }}
                  </slot>
                  <span :class="($q.dark.isActive || $isColorDark(currentSite.primaryColor)) ? 'text-white' : 'text-dark'"> {{ saveLabel }} </span>
                  <action-not-permitted-tooltip v-if="!model.isManagePermitted" />
                </q-btn>
                <q-btn
                  v-if="showAndClose && dialog && !currentSite.isLibrary && model.isManagePermitted"
                  :label="saveLabel + ' and Close'"
                  class="gt-xs"
                  color="primary"
                  :text-color="buttonTextColor"
                  :disable="(!model.isDirty && !creating) || !model.isManagePermitted"
                  :loading="saveAndClosedClicked"
                  @click="save(true)"
                >
                  <action-not-permitted-tooltip v-if="!model.isManagePermitted" />
                </q-btn>
              </template>
            </div>
          </div>

          <q-card-section
            v-if="model.loaded"
            class="form-content row justify-center scroll"
            :class="{ 'q-pa-none': nonePadding }"
            :style="{
              'height': modalType === 'maximized-modal' && (!maximizedHeight || $admin) && $q.screen.gt.xs && !$kiosk ? 'calc(100vh - 100px)' : '100%',
              'border-bottom': btnPositionFixedBottom ? `80px solid ${currentSite.primaryColor}` : undefined
            }"
          >
            <slot
              v-if="showDetail && detail"
              name="detail"
            >
              <div class="row items-center">
                <template v-if="detail">
                  {{ detail }}
                </template>
              </div>
            </slot>
            <div
              class="col"
              :style="{ 'max-width': maxContentWidth }"
            >
              <slot />
            </div>
          </q-card-section>
        </q-form>
      </q-card>
      <q-inner-loading :showing="model.loading && !customSaveFunction" />
      <q-inner-loading
        style="z-index: 1000"
        :showing="customSaveFunction && (saveClicked || saveAndClosedClicked)"
      >
        <q-spinner size="xl" />
        <div v-if="progressIndicatorPercent !== 0">
          {{ progressIndicatorPercent }}%
        </div>
      </q-inner-loading>
    </component>

    <component
      :is="dialog ? 'q-dialog': 'div'"
      v-if="modalType==='modal'"
      v-model="showDialog"
      :maximized="maximized"
      :class="{ 'full-width': !dialog }"
      :no-backdrop-dismiss="model.isDirty"
    >
      <q-card
        :style="{ width: width, 'max-width': maxContentWidth }"
        :bordered="!dialog && !borderless"
        :flat="!dialog"
      >
        <q-form
          ref="formRef"
          greedy
        >
          <model-out-of-date-banner v-if="modelUpdateAvailable" />
          <model-error-banner
            :shown="hasErrors || modelDestroyed || !!$slots.errors"
            dense
          >
            <template v-if="modelDestroyed">
              Model destroyed
            </template>
            <slot
              v-else-if="!!$slots.errors"
              name="errors"
            />
          </model-error-banner>
          <!-- This notice banner is temporary, to prevent confusion until all input fields have been replaced with to ModelFormInput components. -->
          <translation-notice-banner
            v-if="showTranslationNotice"
            :shown="model.isTranslation"
          >
            <slot name="translation-notice" />
          </translation-notice-banner>
          <slot name="header">
            <q-card-section
              class="row items-center"
              :class="dialog ? 'q-mx-xs' : 'bg-responsive-grey-2'"
            >
              <div
                class="text-h6 row items-center"
              >
                <slot name="title">
                  <template v-if="title">
                    {{ title }}
                  </template>
                  <template v-else>
                    {{ actionDisplayName }} {{ modelLabel || model.humanTypeName }}
                  </template>
                  <model-level-icon
                    v-if="currentUser && currentUser.employee"
                    :model="model"
                  />
                </slot>
              </div>
              <translate-language-select
                v-if="shouldShowLanguageSelect"
                :model-value="currentLanguage"
                class="q-ml-sm"
                :loading="model.loading"
                :disable="model.isSubmodel || disableLanguageSelect"
                menu-header-text="Edit Translations"
                @update:model-value="changeTranslationLanguage"
              />
              <q-space />
              <link-to-sites-button
                v-if="canHaveChildSites"
                :model="model"
                :disable="model.isDirty || creating"
                :loading="saveAndClosedClicked"
                @click="showLinkToSitesDialog = true"
              />
              <q-btn
                v-if="!model.isSubmodel"
                class="gt-xs"
                icon="refresh"
                round
                :disable="model.loading || model.dirty"
                @click="fetchUpdatedData()"
              >
                <q-tooltip>Reload Data</q-tooltip>
              </q-btn>
              <q-btn
                v-if="$admin && !model.isSubmodel && model.versioned"
                class="gt-xs"
                icon="history"
                round
                :disable="model.loading || model.dirty"
                @click="showHistory(model.name, model.id)"
              >
                <q-tooltip>View History</q-tooltip>
              </q-btn>

              <q-btn
                v-if="embeddedInModelTable"
                class="q-ml-sm gt-xs"
                dense
                icon="arrow_back"
                :disable="model.loading || model.isDirty || model.isFirst"
                @click="previous()"
              />
              <q-btn
                v-if="embeddedInModelTable"
                class="gt-xs"
                dense
                icon="arrow_forward"
                :disable="model.loading || model.isDirty || model.isLast"
                @click="next()"
              />
              <audit-button
                v-if="model.modelAudits && model.modelAudits.length"
                :model="model"
                @click="$refs.auditDialog.show()"
              />
              <slot name="top-right-action-button">
                <q-btn
                  v-if="dialog"
                  icon="close"
                  flat
                  round
                  dense
                  @click="close(false)"
                />
                <q-btn
                  v-else-if="model.isManagePermitted"
                  :label="saveLabel"
                  class="q-mr-sm"
                  :disable="(!model.isDirty && !creating) || !model.isManagePermitted"
                  color="primary"
                  :text-color="buttonTextColor"
                  :loading="saveClicked"
                  @click="save(closeOnSave)"
                >
                  <q-icon
                    v-if="lockedAndNotOwner"
                    name="lock"
                  />
                  <action-not-permitted-tooltip v-if="!model.isManagePermitted" />
                </q-btn>
              </slot>
            </q-card-section>
          </slot>

          <q-card-section
            v-if="model && (creating || model.loaded || submodel)"
            @keyup.escape="close(false)"
          >
            <slot />
          </q-card-section>
          <q-card-actions
            v-if="(dialog || showFooterActions) && !readOnly"
            class="row justify-end q-ma-sm"
          >
            <slot name="actions" />
            <q-btn
              v-if="model.isManagePermitted"
              :label="saveLabel"
              color="primary"
              :text-color="buttonTextColor"
              :disable="(!model.isDirty && !creating) || !model.isManagePermitted"
              :loading="saveClicked"
              @click="save(closeOnSave)"
            >
              <action-not-permitted-tooltip v-if="!model.isManagePermitted" />
            </q-btn>
          </q-card-actions>
          <q-inner-loading :showing="model.loading" />
        </q-form>
      </q-card>
    </component>
    <slot
      name="link-to-sites-dialog"
      :show-link-to-sites-dialog="showLinkToSitesDialog"
      :toggle-show-link-to-sites-dialog="toggleShowLinkToSitesDialog"
      :circuit-sites-for-library="circuitSitesForLibrary"
    >
      <link-to-sites-dialog
        v-if="currentSite.isLibrary"
        :model-value="showLinkToSitesDialog"
        :circuits-with-sites="circuitSitesForLibrary"
        :model="model"
        @update:model-value="toggleShowLinkToSitesDialog"
      />
    </slot>
    <versions-dialog
      v-if="model.versioned"
      ref="versionsDialog"
    />
    <action-dialog
      ref="modelUpdatedDialog"
      title="New Data Available"
      :message="`${title} has been updated by ${modelUpdatedUser}. It is recommended to discard your changes and update, otherwise ${modelUpdatedUser}'s changes may be overridden`"
      ok-label="Discard Changes and Update"
      ok-color="negative"
      cancel-label="Continue Editing"
      :ok-loading="model.loading"
      :ok-dismiss="false"
      @ok="fetchUpdatedData()"
    />
    <audit-dialog
      v-if="model.modelAudits && model.modelAudits.length"
      ref="auditDialog"
      :model-audits="model.modelAudits"
      @audit-updated="auditUpdated"
    />
  </div>
</template>

<script>
import { MODEL_LEVEL } from 'src/models/Model'

const MODAL_TYPE_OPTIONS = ['in-page', 'maximized-modal', 'modal']

import { appEnv } from 'src/config'
import ModelErrorBanner from 'src/modules/admin/components/ModelForm/components/ModelErrorBanner'
import ModelLevelIcon from 'src/modules/admin/components/ModelLevelIcon'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'
import ActionDialog from 'src/modules/consumer/components/ActionDialog'
import { defineAsyncComponent } from 'vue'

function getModelTableAncestorComponent (modelFormComponent, modelFormAncestorComponent) {
  const currentComponent = modelFormAncestorComponent || modelFormComponent

  if (currentComponent.$options.name === 'ModelTable') {
    // We only care about the first model table ancestor we find.
    if (currentComponent.collection.typeName === modelFormComponent.model.typeName) {
      // If the first model table ancestor has a collection of the same model as
      // our model form, that's the model table we're looking for.
      return currentComponent
    } else {
      // Otherwise, we're embadded in the model table for a different model, so
      // we can stop trying to resolve the model table ancestor.
      return null
    }
  } else if (currentComponent.$parent) {
    // If the current component isn't a model table, but it has a parent, let's
    // check the parent.
    return getModelTableAncestorComponent(modelFormComponent, currentComponent.$parent)
  }
}

// ModelForm is used in many places, but not all of its subcomponents are rendered in each case.
// To reduce JS bundle size, we lazy-load components only when they are actually used.
export default {
  name: 'ModelForm',
  components: {
    ActionNotPermittedTooltip: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/ActionNotPermittedTooltip')),
    LinkToSitesDialog: defineAsyncComponent(() => import('src/modules/admin/components/LinkToSitesDialog')),
    LinkToSitesButton: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/LinkToSitesButton')),
    VersionsDialog: defineAsyncComponent(() => import('src/modules/admin/versions/VersionsDialog')),
    ActionDialog,
    AuditDialog: defineAsyncComponent(() => import('src/modules/admin/components/AuditDialog')),
    AuditButton: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/AuditButton')),
    ModelErrorBanner,
    ModelLevelIcon,
    ModelOutOfDateBanner: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/ModelOutOfDateBanner')),
    TakeEditLockButton: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/TakeEditLockButton')),
    ViewingUsers: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/ViewingUsers')),
    TranslateLanguageSelect: defineAsyncComponent(() => import('src/modules/admin/components/TranslateLanguageSelect')),
    TranslationNoticeBanner: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/TranslationNoticeBanner'))
  },
  mixins: [FormValidationMixin],
  provide () {
    return {
      model: this.model
    }
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    modalType: {
      type: String,
      required: false,
      default: 'maximized-modal',
      validator: function (value) {
        return _.includes(MODAL_TYPE_OPTIONS, value)
      }
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    maximizedWidth: {
      type: String,
      required: false,
      default: '100%'
    },
    revertLabel: {
      type: String,
      required: false,
      default: 'Revert'
    },
    maximizedHeight: {
      type: String,
      required: false,
      default: '100%'
    },
    maxContentWidth: {
      type: String,
      required: false,
      default: '1024px'
    },
    maximized: {
      type: Boolean,
      required: false,
      default: false
    },
    submodel: {
      type: Boolean,
      required: false,
      default: false
    },
    width: {
      type: String,
      required: false,
      default: '500px'
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    detail: {
      type: String,
      required: false,
      default: null
    },
    handledActions: {
      type: Array,
      required: false,
      default () {
        return ['add', 'edit']
      }
    },
    showAndClose: {
      type: Boolean,
      required: false,
      default: true
    },
    closeOnSave: {
      type: Boolean,
      required: false,
      default: true
    },
    hideTitle: {
      type: Boolean,
      required: false,
      default: false
    },
    hideHeaderButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    disableLanguageSelect: {
      type: Boolean,
      required: false,
      default: false
    },
    btnRound: {
      type: Boolean,
      required: false,
      default: false
    },
    btnOutline: {
      type: Boolean,
      required: false,
      default: false
    },
    btnLarge: {
      type: Boolean,
      required: false,
      default: false
    },
    btnShowTitle: {
      type: Boolean,
      required: false,
      default: false
    },
    btnFloatingRight: {
      type: Boolean,
      required: false,
      default: false
    },
    btnPositionFixedBottom: {
      type: Boolean,
      required: false,
      default: false
    },
    btnPositionAbsoluteBottomLeft: {
      type: Boolean,
      required: false,
      default: false
    },
    centered: {
      type: Boolean,
      required: false,
      default: false
    },
    flexGrow: {
      type: Boolean,
      required: false,
      default: true
    },
    showDetail: {
      type: Boolean,
      required: false,
      default: false
    },
    showTranslationNotice: {
      type: Boolean,
      required: false,
      default: false
    },
    validate: {
      type: Function,
      required: false,
      default: null
    },
    emitOnlyCreate: {
      type: Boolean,
      required: false,
      default: false
    },
    lockable: {
      type: Boolean,
      required: false,
      default: false
    },
    zoom: {
      type: Number,
      required: false,
      default: 1
    },
    beforeSave: {
      type: Function,
      required: false,
      default: null
    },
    customSaveFunction: {
      type: Function,
      required: false,
      default: null
    },
    modelLabel: {
      type: String,
      required: false,
      default: null
    },
    nonePadding: {
      type: Boolean,
      required: false,
      default: false
    },
    skipParentPropogation: {
      type: Boolean,
      required: false,
      default: false
    },
    isDialog: {
      type: Boolean,
      required: false,
      default: true
    },

    showFooterActions: {
      type: Boolean,
      required: false,
      default: false
    },
    borderless: {
      type: Boolean,
      required: false,
      default: false
    },
    noClosedHooksSetup: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value', 'on-show', 'validation-error', 'create', 'model-created', 'model-saved', 'add-model', 'apply-changes', 'change', 'revert', 'on-close'],
  data () {
    const modelTable = getModelTableAncestorComponent(this)
    const embeddedInModelTable = !!modelTable
    return {
      dialog: this.isDialog,
      showDialog: false,
      saveClicked: false,
      saveAndClosedClicked: false,
      id: null,
      modelTable,
      embeddedInModelTable,
      hasErrors: false,
      showLinkToSitesDialog: false,
      modelDestroyed: false,
      modelLocked: true,
      lockedUserId: null,
      allViewingUsers: [],
      lastLockRequestSuccessful: true,
      modelUpdatedUser: '',
      modelUpdateAvailable: false,
      showAudits: false,
      progressIndicatorPercent: 0,
      isClosedHooksSetup: this.noClosedHooksSetup
    }
  },
  computed: {
    creating () {
      if (this.submodel) {
        if (this.model.id > 0) {
          return false
        } else if (this.model.isInCollection) {
          return false
        } else {
          return true
        }
      } else {
        return !this.model.id || this.model.id < 0
      }
    },
    modelLockable () {
      return this.lockable && this.lastLockRequestSuccessful
    },
    lockedAndNotOwner () {
      if (this.modelLocked && this.editingUser) {
        return this.lockedUserId !== this.currentUser.id
      } else {
        return false
      }
    },
    haveLock () {
      return this.modelLocked && this.editingUser && this.lockedUserId === this.currentUser.id
    },
    editingUser () {
      return _.find(this.allViewingUsers, user => user.locked)
    },
    actionDisplayName () {
      let action = ''
      if (this.creating) {
        if (this.submodel) {
          action = 'Add'
        } else {
          action = 'Creating'
        }
      } else if (this.readOnly) {
        action = ''
      } else {
        action = 'Edit'
      }

      if (this.currentSite.isLibrary) {
        action = action + ' Library'
      }

      return action
    },
    saveLabel () {
      if (this.creating) {
        if (this.submodel) {
          return 'Add'
        } else {
          return 'Create'
        }
      } else {
        if (this.submodel) {
          return 'Update'
        } else {
          return 'Save'
        }
      }
    },
    canHaveChildSites () {
      return !!(this.currentSite.isLibrary && this.model.libraryDescendantSites)
    },
    circuitSitesForLibrary () {
      if (!this.currentSite.isLibrary) {
        return []
      }

      let sites = []
      if (!this.currentCircuit.isLibrary) {
        sites = this.currentCircuit.sites.data.map(site => {
          if (site.id === this.currentSite.id) {
            return null
          }
          return site
        })

        sites = _.compact(sites)
      } else {
        sites = _.compact(_.map(this.currentUser.authorizedSites.data, site => {
          if (site.id === this.currentSite.id) {
            return null
          }
          return site
        }))
      }

      return _.groupBy(_.sortBy(sites, 'circuit.name'), 'circuit.name')
    },
    viewingUsersNotMe () {
      return _.filter(this.allViewingUsers, user => user.id !== this.currentUser.id.toString())
    },
    buttonTextColor () {
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    },
    currentLanguage () {
      return this.model.language
    },
    shouldDisableLanguageSelect () {
      return this.submodel || this.disableLanguageSelect
    },
    shouldShowLanguageSelect () {
      return this.$admin &&
        this.currentCircuit.supportsTranslations &&
        this.model.isRemote &&
        this.model.isTranslationEditingAllowed &&
        this.model.isTranslatable
    },
    buttonWidth () {
      if (this.btnPositionAbsoluteBottomLeft) return '28%'
      if (this.btnPositionFixedBottom) return '94%'
      return undefined
    },
    isAppView () { return !!window._cordovaNative }
  },
  watch: {
    'model.id' (_newId, oldId) {
      this.hasErrors = false
      if (oldId && !this.model.creating && this.$modelFormLock && this.modelLockable) {
        this.$modelFormLock.leaveForm(this.lockName(oldId))
        this.updateLockStatus()
      }
    },
    showDialog () {
      this.syncParentTableLanguage()
      // setup / destroy closed hooks to prevent memory leaks
      if (!this.showDialog && this.isClosedHooksSetup) {
        this.destroyClosedHooks()
      } else if (!this.isClosedHooksSetup) {
        this.setupClosedHooks()
      }
      this.$emit('update:model-value', this.showDialog)
      this.setupOrDestroyLock()
    },
    'model.isDirty' () {
      if (this.model.isDirty) {
        this.handleStartToEdit()
      } else {
        this.handleNotDirty()
      }
    }
  },
  context: ['currentUser'],
  configuration: ['currentSite', 'currentCircuit'],
  beforeUnmount () {
    this.formDestroyRequested = true
    this.leaveForm()
    if (this.$modelFormLock) {
      this.$modelFormLock.handleLockNotification = null
    }
    clearInterval(this.destroyCheckInterval)
    this.destroyClosedHooks()
  },
  created () {
    _.extend(this, { MODEL_LEVEL })

    this.setupClosedHooks()

    if (this.modelTable) {
      // Register this form for handling actions
      this.registerSelfToParentTableIfNoneRegistered()
    } else if (this.$parent.$options.name === 'QPage' || this.$parent.$parent.$options.name === 'QPageContainer' || this.$parent.$parent.$options.name === 'QExpansionItem') {
      this.dialog = false
      if (this.$parent.show && !this.skipParentPropogation) {
        this.$parent.show()
      } else if (this.$parent.$parent.show && !this.skipParentPropogation) {
        this.$parent.$parent.show()
      } else {
        this.show()
      }
    }

    this.destroyCheckInterval = setInterval(() => {
      if (this.model?.destroyed) {
        this.modelDestroyed = true
      }
    }, 500)
  },
  unmounted () {
    this.unregisterSelfFromParentTable()
  },
  methods: {
    registerSelfToParentTableIfNoneRegistered () {
      if (!this.modelTable) return

      _.each(this.handledActions, action => {
        if (_.isNil(this.modelTable.actionForms[action])) {
          this.modelTable.setActionForm(action, this)
        }
      })
    },
    unregisterSelfFromParentTable () {
      if (!this.modelTable) return

      _.each(this.handledActions, action => {
        this.modelTable.removeActionForm(action, this)
      })
    },
    async changeTranslationLanguage (language) {
      const changeLanguage = () => {
        this.model.resetToLanguage(language)
        this.fetchUpdatedData()
      }
      if (this.model.isDirty) {
        this.showUnsavedChangesPrompt(changeLanguage)
      } else {
        changeLanguage()
      }
    },
    lockName (id = null) {
      return (this.$options.name + (this.model.typeName + (id || this.model.id))).replace(/[^a-zA-Z0-9]/g, '')
    },
    async fetchUpdatedData () {
      try {
        const result = await this.model.fetch(true)
        if (result?.errors?.length) {
          this.$mh.showErrors(result.errors)
        } else {
          this.modelUpdateAvailable = false
        }
      } catch (exception) {
        console.log(exception)
        this.$mh.showErrors(['Unable to fetch updated data'])
      }

      this.$refs.modelUpdatedDialog.hide()
    },
    setupOrDestroyLock () {
      if (!this.showDialog) {
        this.formDestroyRequested = true
        this.leaveForm()
      } else if (!this.model.creating && this.$modelFormLock && this.modelLockable) {
        this.$modelFormLock.handleLockNotification = (data) => {
          if (data.data.modelFormLockSent.modelFormLock.formName === this.lockName()) {
            this.updateLockStatus({ data: { sendModelFormLock: data.data.modelFormLockSent.modelFormLock } })
          }
        }
        this.updateLockStatus()
      }
    },
    async auditUpdated () {
      await this.model.fetch(true)
      if (this.model.archivedAt) {
        this.$refs.auditDialog.hide()
        this.close()
      }
    },
    handleStartToEdit () {
      if (!this.modelLockable) return

      if (this.haveLock) return

      if (!this.editingUser) {
        this.lock()
      } else {
        this.$q.dialog({
          title: 'Take Lock For Editing',
          style: 'width: auto',
          message: `Would you like to take editing from ${this.editingUser.firstName} ${this.editingUser.lastName}?`,
          ok: {
            label: 'Take Edit Lock',
            color: 'red'
          },
          cancel: {
            label: 'Go Back',
            color: 'secondary'
          },
          focus: 'cancel'
        }).onOk(() => {
          this.lock()
        }).onCancel(() => {
          this.revert()
        })
      }
    },
    handleNotDirty () {
      if (!this.modelLockable) return

      if (!this.haveLock) return

      this.releaseLock()
    },
    async lock () {
      if (!this.modelLockable) return

      const lockResult = await this.$modelFormLock.lockForm(this.lockName())

      if (lockResult.errors) {
        this.lastLockRequestSuccessful = false
        return
      } else {
        this.lastLockRequestSuccessful = true
      }

      this.modelLocked = lockResult.modelLocked
      this.lockedUserId = lockResult.lockedUserId
      this.allViewingUsers = lockResult.allViewingUsers
    },
    async releaseLock () {
      if (!this.haveLock) return

      const lockResult = await this.$modelFormLock.releaseLock(this.lockName())

      if (lockResult.errors) {
        this.lastLockRequestSuccessful = false
        return
      } else {
        this.lastLockRequestSuccessful = true
      }

      this.modelLocked = lockResult.modelLocked
      this.lockedUserId = lockResult.lockedUserId
      this.allViewingUsers = lockResult.allViewingUsers
    },
    async updateLockStatus (cableNotification = null) {
      if (!this.modelLockable) return

      let lockResult = await this.$modelFormLock.viewForm(this.lockName(), cableNotification)

      if (lockResult.errors) {
        this.lastLockRequestSuccessful = false
        return
      } else {
        this.lastLockRequestSuccessful = true
      }

      let lockedUser = lockResult.lockedUserId

      if (lockResult.modelUpdated && lockResult.modelUpdatedUserId !== this.currentUser.id && this.model.isDirty) {
        this.modelUpdatedUser = lockResult.modelUpdatedUserString
        this.modelUpdateAvailable = true
        this.$refs.modelUpdatedDialog.show()
        return
      } else if (lockResult.modelUpdated && lockResult.modelUpdatedUserId !== this.currentUser.id) {
        this.$mh.notify(lockResult.modelUpdatedUserString + ' has made some changes')
        this.fetchUpdatedData()
        return
      }

      if (cableNotification && this.lockedUserId === this.currentUser.id && lockedUser !== this.currentUser.id) {
        // Another tab was closed, try to restablish lock
        lockResult = await this.$modelFormLock.viewForm(this.lockName())
      } else if (cableNotification && !_.find(lockResult.allViewingUsers, user => user.id === this.currentUser.id.toString())) {
        // Another tab was closed and not current lock owner, tell server still viewing
        lockResult = await this.$modelFormLock.viewForm(this.lockName())
      }
      lockedUser = lockResult.lockedUserId
      const lockOwnershipRevoked = this.lockedUserId === this.currentUser.id && lockedUser !== this.currentUser.id
      this.modelLocked = lockResult.modelLocked
      this.lockedUserId = lockedUser
      this.allViewingUsers = lockResult.allViewingUsers

      if (lockOwnershipRevoked) {
        const newUser = _.find(this.allViewingUsers, user => user.locked)
        if (newUser) {
          this.$mh.showErrors([`Editing lock has been taken by ${newUser.firstName} ${newUser.lastName}`])
        } else if (!this.formDestroyRequested) {
          this.$mh.showErrors(['Editing lock has been lost due to internet disconnection'])
        }
      }
    },
    async leaveForm () {
      if (!this.modelLockable || !this.$modelFormLock) return

      this.$modelFormLock.leaveForm(this.lockName())
    },
    show (model, initialData) {
      this.modelDestroyed = false
      this.$emit('on-show')
      if (this.$parent.beforeShow) this.$parent.beforeShow()
      if (model && model.isModel) {
        if (model.constructor.publishable && model.draftId) {
          const draftModel = new model.constructor()
          draftModel.id = model.draftId
          if (model.language !== draftModel.language) {
            draftModel.resetToLanguage(model.language)
          }
          this.model.proxy(draftModel)
          if (!this.submodel && this.model.id > 0) {
            this.fetchUpdatedData()
          }
        } else {
          this.model.proxy(model)
          if (!this.submodel && this.model.id > 0) {
            this.fetchUpdatedData()
          }
        }
      } else if (model && model.id && model.id > 0) {
        this.model.id = model.id
        this.fetchUpdatedData()
      } else {
        // Connect TableModel's Collectction to this model (used for adding new)
        if (this.modelTable) {
          this.model.setParent(this.modelTable.collection)
        }
        this.model.reset()
        if (initialData) {
          this.model.setData(initialData)
        }
      }
      this.trackShow()
      this.showDialog = true
    },
    showUnsavedChangesPrompt (onOkRevert = null, onCancel = null) {
      const dialog = this.$q.dialog({
        title: 'Unsaved changes',
        style: 'width: auto',
        message: this.model.fieldsChangedHtmlMessage.join('\n'),
        html: true,
        ok: {
          label: 'Discard Changes',
          color: 'primary'
        },
        cancel: {
          label: 'Go Back',
          color: 'secondary'
        },
        focus: 'cancel'
      }).onOk(() => {
        this.revert()
        if (onOkRevert) {
          onOkRevert()
        }
      })
      if (onCancel) {
        dialog.onCancel(onCancel)
      }
    },
    async runAuditAction (audit, actionName) {
      audit.action = actionName
      await audit.save()
      audit.resetField('action')
      await this.model.fetch(true)
      if (this.model.archivedAt) {
        this.close()
      }
    },
    preventNavigation (e) {
      const currentlyEditing = this.showDialog || !this.dialog

      if (!currentlyEditing) return undefined

      if (!this.model.isDirty) return undefined

      if (appEnv === 'development') return undefined

      e.preventDefault()
      e.returnValue = ''
    },
    next () {
      this.show(this.model.next)
    },
    previous () {
      this.show(this.model.previous)
    },
    showHistory () {
      this.$refs.versionsDialog.show(this.model)
    },
    triggerValidate () {
      this.validateForm(
        // Success callback
        null,
        // Failure callback
        () => {
          this.showErrorBanner()
        })
    },
    showErrorBanner () {
      this.hasErrors = true
      this.clearErrorMessage && clearTimeout(this.clearErrorMessage)
      this.clearErrorMessage = setTimeout(() => { this.hasErrors = false }, 3000)
    },
    setupClosedHooks () {
      // Register layout rarely uses routes, causes DOM node leak if used
      if (this.$register) return

      if (this.isClosedHooksSetup) return

      this.isClosedHooksSetup = true

      this.$router.beforeEach((_to, _from, next) => {
        const currentlyEditing = this.showDialog || !this.dialog

        if (this.model.isDirty && currentlyEditing) {
          const onOkRevert = () => {
            this.showDialog = false
            next()
          }
          const onCancel = () => {
            next(false)
          }
          this.showUnsavedChangesPrompt(onOkRevert, onCancel)
        } else {
          next()
        }
      })

      window.addEventListener('beforeunload', this.preventNavigation)
    },
    destroyClosedHooks () {
      if (!this.isClosedHooksSetup) return

      window.removeEventListener('beforeunload', this.preventNavigation)

      this.isClosedHooksSetup = false
    },

    async save (andClose = true) {
      const wasCreating = this.creating
      this.hasErrors = false

      this.validateForm(
        // Success callback
        async () => {
          let validateConfigurationBeforeFetchingCollection = false

          this.trackSave()
          if (andClose) {
            this.saveAndClosedClicked = true
          } else {
            this.saveClicked = true
          }

          if (this.emitOnlyCreate && wasCreating) {
            this.$emit('create')
          } else if (!this.submodel) {
            validateConfigurationBeforeFetchingCollection = _(this.model.describeDirty).keys().some(key => {
              return this.model[key]?.constructor?.isConfiguration
            })
            // Actually do the save
            if (this.beforeSave) {
              await this.beforeSave(this.model)
            }
            let result

            if (this.customSaveFunction) {
              this.progressIndicatorPercent = 0
              result = await this.customSaveFunction.call(this.model, (percent) => {
                this.progressIndicatorPercent = percent
              })
            } else {
              result = await this.model.save()
            }

            if (result.errors.length) {
              this.$mh.showErrors(result.errors)
              if (andClose) {
                this.saveAndClosedClicked = false
              } else {
                this.saveClicked = false
              }
              return
            }

            if (wasCreating) {
              this.id = this.model.id
              this.$emit('model-created')
            }

            this.$emit('model-saved')
          }
          if (wasCreating) {
            if (this.submodel && !this.model.parent.isCollection) {
              throw new Error('Expected parent to be collection')
            }

            if (this.model.parent) {
              this.model.parent.add(this.model.clone())
              this.$emit('add-model', this.model)
            }
          } else {
            this.model.applyChanges()
            this.$emit('apply-changes')
          }
          this.$emit('change')
          this.modelTable && this.modelTable.handleChange(this.currentLanguage, validateConfigurationBeforeFetchingCollection)
          if (this.modelLockable && this.$modelFormLock) {
            this.$modelFormLock.savedChangeToModel(this.lockName())
          }

          if (andClose) {
            this.saveAndClosedClicked = false
            this.close()
          } else {
            this.saveClicked = false
          }
        },
        // Failure callback
        () => {
          this.hasErrors = true
          this.$emit('validation-error')
          this.clearErrorMessage && clearTimeout(this.clearErrorMessage)
          this.clearErrorMessage = setTimeout(() => { this.hasErrors = false }, 3000)
        })
    },
    close (saving = true) {
      if (this.submodel) {
        this.showDialog = false
        this.$emit('on-close')
        return
      }

      // Not dirty, just close
      if (!this.model.isDirty) {
        this.showDialog = false
        return
      }

      if (this.creating && this.emitOnlyCreate && saving) {
        this.showDialog = false
        return
      }
      // Need to set timeout because the ESC key that invoked this dialog
      // would immediately close this one
      setTimeout(() => {
        this.showUnsavedChangesPrompt(() => {
          this.showDialog = false
        })
      })
    },
    // For now, the ModelTable's collection and this.model are closely tied, and in some cases will update
    // each other's language. It's less confusing if we remain consistent and always keep the parent table'
    // language in sync with this form's language. When we close the dialog, we update the table's language
    // if it was changed in the form.
    syncParentTableLanguage () {
      if (!this.showDialog && this.modelTable && this.modelTable.currentLanguage !== this.currentLanguage) {
        this.modelTable.handleChange(this.currentLanguage)
      }
    },
    computeAuditLevel (audits) {
      const filteredAudits = _.filter(audits, audit => {
        if (audit.suppressed) {
          return false
        }

        if (!this.currentUser?.superUser && audit.indyOnly) {
          return false
        }
        return true
      })

      if (_.some(filteredAudits, { level: 'error' })) {
        return 'error'
      }

      if (_.some(filteredAudits, { level: 'warning' })) {
        return 'warning'
      }
      return 'info'
    },
    exposeModel () {
      window.model = this.model
    },
    trackShow () {
      const id = this.model.isLocal ? undefined : this.model.id
      const name = this.$parent.$options.name || (this.model.typeName + 'Form')
      this.$trackPage(this, {
        name,
        creating: this.model.isLocal,
        [this.model.variableName + 'Id']: id,
        id
      })
    },
    trackSave () {
      const id = this.model.isLocal ? undefined : this.model.id
      this.$trackEvent(
        this.$indyUserEvents.MODEL_SAVED(this.model.typeName),
        {
          id,
          [this.model.variableName + 'Id']: id,
          name: this.model.name || undefined,
          creating: this.model.isLocal
        }
      )
    },
    revert () {
      this.model.revert()
      this.$emit('revert')
    },
    toggleShowLinkToSitesDialog (value) {
      this.showLinkToSitesDialog = value
    }
  }
}

</script>

<style lang="scss" scoped>
.form-content {
  width: 100%;
}

.circle-with-text {
  border-radius: 100%;
  text-align: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: solid 1px #444;
  font-size: 10px;
  font-weight: 500;
  line-height: 21px;
}
</style>
